import React, { useState, useEffect } from 'react';
import { Grid, makeStyles, TextField, Typography } from '@material-ui/core';

import EditCard from 'components/EditCard';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import { useTranslation } from 'react-i18next';
import { notificationActions } from 'actions/notificationActions';

import { AxiosError } from 'axios';
import { departmentActions } from 'actions/departmentActions';
import { buttonInactiveColor } from 'theme/theme';

import { customerNumberRE } from 'helpers/validation';

const useStyles = makeStyles(() => ({
  explainer: {
    color: buttonInactiveColor(),
  },
}));

const CustomerNumberCard: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState({ customerNumber: '' });
  const [isMounted, setIsMounted] = useState(true);

  const department = useSelector(
    (state: ApplicationState) => state.department?.department,
  );

  // track unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // track organization
  useEffect(() => {
    resetInput();
  }, [department]);

  const resetInput = () => {
    if (department) {
      setInput({ customerNumber: department.customerNumber ?? '' });
    } else {
      setInput({ customerNumber: '' });
    }
  };

  const handleCustomerNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (
      value.length > input.customerNumber.length && // always allow character deletion
      !customerNumberRE.test(value)
    ) {
      return;
    }
    setInput((input) => ({ ...input, customerNumber: value }));
  };

  const saveHandler = (error?: AxiosError) => {
    if (isMounted) {
      setIsLoading(false);
      setEditMode(!!error);
    }
    if (error) {
      dispatch(notificationActions.enqueueErrorResponse(error));
    }
  };

  const handleSave = () => {
    if (department && input) {
      setIsLoading(true);
      dispatch(
        departmentActions.updateCustomerNumber(
          department.departmentId,
          input.customerNumber.length === 0 ? null : input.customerNumber,
          saveHandler,
        ),
      );
    }
  };

  const handleEdit = () => {
    resetInput();
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  return (
    <EditCard
      editMode={editMode}
      isLoading={isLoading}
      title={t('department_page.contract.customer_number')}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
      requireGlobalAdmin
    >
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="normal"
              fullWidth
              label={t('department_page.contract.customer_number')}
              name="customerNumber"
              value={input?.customerNumber ?? ''}
              onChange={handleCustomerNumberChange}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <div className={classes.explainer}>
            {t('department_page.contract.customer_number_explainer')}
          </div>
          <Typography variant="body1">
            {department?.customerNumber ?? ''}
          </Typography>
        </>
      )}
    </EditCard>
  );
};

export default CustomerNumberCard;
